/**
 * LoopbackLink.ts — a simulated network between a client and the authoritative
 * host, living in-process. It delays messages by a configurable latency (+jitter)
 * and randomly drops a fraction, so the two-tab / single-page loopback demo can
 * SHOW client prediction and remote interpolation coping with a bad connection —
 * exactly the conditions the cloud version (Phase 3) will face for real.
 *
 * `up` = client → host (inputs). `down` = host → client (snapshots/events).
 * Deterministic when given a seeded `rand` (the unit tests do this); the live
 * demo passes Math.random for natural-looking jitter. Either way this only
 * reorders/drops MESSAGES — it never touches the simulation, which stays exactly
 * deterministic on both ends.
 */
export interface LinkConfig {
  /** One-way latency in milliseconds. */
  latencyMs: number;
  /** ± random jitter added to each message's latency. */
  jitterMs: number;
  /** Fraction of messages dropped, 0..1. */
  loss: number;
}

export type LinkDir = 'up' | 'down';

interface Queued {
  at: number;
  dir: LinkDir;
  payload: unknown;
}

export class LoopbackLink {
  private queue: Queued[] = [];
  private nowMs = 0;

  constructor(
    private cfg: LinkConfig,
    private readonly rand: () => number = Math.random,
  ) {}

  setConfig(cfg: Partial<LinkConfig>): void {
    this.cfg = { ...this.cfg, ...cfg };
  }

  sendUp(payload: unknown): void {
    this.enqueue('up', payload);
  }

  sendDown(payload: unknown): void {
    this.enqueue('down', payload);
  }

  private enqueue(dir: LinkDir, payload: unknown): void {
    if (this.cfg.loss > 0 && this.rand() < this.cfg.loss) return; // dropped
    const jitter = (this.rand() * 2 - 1) * this.cfg.jitterMs;
    const at = this.nowMs + Math.max(0, this.cfg.latencyMs + jitter);
    this.queue.push({ at, dir, payload });
  }

  /** Advance the link clock and deliver every message now due, in time order. */
  tick(dtMs: number, onUp: (p: unknown) => void, onDown: (p: unknown) => void): void {
    this.nowMs += dtMs;
    if (this.queue.length === 0) return;
    this.queue.sort((a, b) => a.at - b.at);
    let i = 0;
    while (i < this.queue.length && this.queue[i].at <= this.nowMs) i++;
    const due = this.queue.splice(0, i);
    for (const m of due) (m.dir === 'up' ? onUp : onDown)(m.payload);
  }
}
